import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import leadService, { Lead, LeadInput, LeadActivity, type LeadStatusReasonInput } from '@/services/lead.service';
import { documentKeys } from '@/hooks/useDocuments';

export const LEAD_KEYS = {
  all: ['crm', 'leads'] as const,
  lists: () => [...LEAD_KEYS.all, 'list'] as const,
  list: (filters?: Record<string, string | boolean | undefined>) => [...LEAD_KEYS.lists(), filters] as const,
  details: () => [...LEAD_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...LEAD_KEYS.details(), id] as const,
  activities: (id: string) => [...LEAD_KEYS.detail(id), 'activities'] as const,
};

export function useLeads(filters?: {
  lead_status?: string;
  assigned_to?: string;
  priority?: string;
  search?: string;
  sales_team_id?: string;
  missing_team?: boolean;
}) {
  return useQuery({
    queryKey: LEAD_KEYS.list(filters),
    queryFn: () => leadService.getAll(filters),
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: LEAD_KEYS.detail(id!),
    queryFn: () => leadService.getById(id!),
    enabled: !!id,
  });
}

export function useLeadActivities(leadId: string | undefined) {
  return useQuery({
    queryKey: LEAD_KEYS.activities(leadId!),
    queryFn: () => leadService.getActivities(leadId!),
    enabled: !!leadId,
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (data: LeadInput) => leadService.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.lists() });
      message.success('Lead created');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to create lead'),
  });
}

export function useUpdateLead() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LeadInput> }) => leadService.update(id, data),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.lists() });
      qc.invalidateQueries({ queryKey: LEAD_KEYS.detail(v.id) });
      message.success('Lead updated');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to update lead'),
  });
}

export function useDeleteLead() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (id: string) => leadService.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.lists() });
      message.success('Lead deleted');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to delete lead'),
  });
}

export function useUpdateLeadStatus() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: string; reason?: LeadStatusReasonInput }) =>
      leadService.updateStatus(id, status, reason),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.lists() });
      qc.invalidateQueries({ queryKey: LEAD_KEYS.detail(v.id) });
      qc.invalidateQueries({ queryKey: LEAD_KEYS.activities(v.id) });
      message.success('Status updated');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to update status'),
  });
}

/** Optimistically move a lead to a new status in list cache. */
export function patchLeadStatusInListCache(
  qc: ReturnType<typeof useQueryClient>,
  filters: Record<string, string | boolean | undefined> | undefined,
  leadId: string,
  newStatus: string
) {
  qc.setQueryData<Lead[]>(LEAD_KEYS.list(filters), (old) => {
    if (!old) return old;
    return old.map((l) => (l.id === leadId ? { ...l, lead_status: newStatus } : l));
  });
}

export function useAssignLead() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, user_id }: { id: string; user_id: string }) => leadService.assign(id, user_id),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.lists() });
      qc.invalidateQueries({ queryKey: LEAD_KEYS.detail(v.id) });
      message.success('Lead assigned');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to assign'),
  });
}

export function useConvertLead() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, opportunity_id }: { id: string; opportunity_id: string }) =>
      leadService.convertToOpportunity(id, opportunity_id),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: LEAD_KEYS.lists() });
      qc.invalidateQueries({ queryKey: LEAD_KEYS.detail(v.id) });
      qc.invalidateQueries({ queryKey: ['crm', 'opportunities'] });
      qc.invalidateQueries({ queryKey: documentKeys.byEntity('Opportunity', v.opportunity_id) });
      qc.invalidateQueries({ queryKey: documentKeys.byEntity('Lead', v.id) });
      message.success('Lead linked to opportunity');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to convert'),
  });
}

export type { Lead, LeadInput, LeadActivity };
